/**
 * Diálogo para cambiar la foto de una máquina (`images/maquinas`) o de un lado
 * (`images/maquinas_lado`).
 *
 * Compartido por las dos pantallas del grupo Avanzado: sólo cambia el texto y la
 * URL de la imagen actual. La subida va en binario al endpoint del recurso.
 */

import { Image as ImageIcon } from 'lucide-react';
import { useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';

export function FotoDialogo({
  titulo,
  descripcion,
  urlActual,
  altActual,
  subiendo,
  error,
  onSubir,
  onCerrar,
}: {
  titulo: string;
  descripcion: string;
  /** URL pública de la foto guardada (o `null` si no tiene). */
  urlActual: string | null;
  altActual: string;
  subiendo: boolean;
  error: string | null;
  onSubir: (archivo: File) => void;
  onCerrar: () => void;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [vista, setVista] = useState<string | null>(null);
  const [unoRoto, setUnoRoto] = useState(false);

  const elegir = (seleccionado: File | null) => {
    setArchivo(seleccionado);
    setVista(seleccionado ? URL.createObjectURL(seleccionado) : null);
  };

  const mostrarActual = urlActual !== null && !unoRoto;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={titulo}
      description={descripcion}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={subiendo}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={subiendo}
            disabled={!archivo}
            onClick={() => archivo && onSubir(archivo)}
          >
            Subir foto
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo subir">{error}</Alert> : null}

        <div className="flex items-center gap-4">
          <div className="flex size-24 items-center justify-center overflow-hidden rounded-card bg-ink-50 ring-1 ring-ink-100">
            {vista ? (
              <img src={vista} alt="Vista previa" className="size-full object-cover" />
            ) : mostrarActual ? (
              <img
                src={urlActual}
                alt={altActual}
                className="size-full object-cover"
                onError={() => setUnoRoto(true)}
              />
            ) : (
              <ImageIcon className="size-6 text-ink-300" aria-hidden="true" />
            )}
          </div>

          <div className="flex flex-col gap-2">
            <input
              ref={entrada}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(evento) => elegir(evento.target.files?.[0] ?? null)}
            />
            <Button variant="outline" size="sm" onClick={() => entrada.current?.click()}>
              Elegir imagen
            </Button>
            <p className="text-xs text-ink-400">JPG, PNG o WEBP · máximo 4 MB</p>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
